import {FC, useState, useEffect, createContext, useContext, Dispatch, SetStateAction} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers.amplify'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  getCurrentUser: () => void
  isAdmin: () => boolean
  isStockClerk: () => boolean
  isStockManager: () => boolean
  isSuperAdmin: () => boolean
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  getCurrentUser: () => {},
  isSuperAdmin: () => {
    return false
  },
  isAdmin: () => {
    return false
  },
  isStockClerk: () => {
    return false
  },
  isStockManager: () => {
    return false
  },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    authHelper.logout()
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  const getCurrentUser = async () => {
    const {authModel, userModel} = await authHelper.getCurrentUser()
    setCurrentUser(userModel)
    setAuth(authModel)
  }

  const isSuperAdmin = () => {
    return currentUser?.groups?.includes('SuperAdmin') || currentUser?.groups?.includes('grpSuperAdmin') || false
  }

  const isAdmin = () => {
    return currentUser?.groups?.includes('Admin') || false
  }

  const isStockClerk = () => {
    return currentUser?.groups?.includes('StockClerk') || false
  }

  const isStockManager = () => {
    return currentUser?.groups?.includes('StockManager') || false
  }

  return <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout, getCurrentUser, isSuperAdmin, isAdmin, isStockClerk, isStockManager}}>{children}</AuthContext.Provider>
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, getCurrentUser} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser()
      } catch (error) {
        logout()
        setShowSplashScreen(false)
      } finally {
        setShowSplashScreen(false)
      }
    }
    if (auth && auth.api_token) {
      checkAuth()
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
